import type { RewardsPrice } from "@hopper-b2b/types";
import type { PointsBalanceType } from "@/modules/account";

interface CalculateMaxApplicablePointsParams {
  pointsBalance: PointsBalanceType | undefined;
  rewardsAccountId: string | undefined;
  isFixedPrice: boolean;
  baseFareFixedRewardsPrice: RewardsPrice | undefined;
  /**
   * Authoritative max redeemable points for the quote, from
   * POST /api/v0/pointsToCash/quote. The backend already nets wallet
   * offers and credits (RBC-2289), so this is the final redeemable ceiling
   * (still clamped by the user's available balance). If it is absent
   * (still loading or the fetch failed) there is no redemption.
   */
  pointsRedemptionMaximum?: number;
}

export const calculateMaxApplicablePoints = ({
  pointsBalance,
  rewardsAccountId,
  isFixedPrice,
  baseFareFixedRewardsPrice,
  pointsRedemptionMaximum,
}: CalculateMaxApplicablePointsParams):
  | Omit<PointsBalanceType, "basePointsToCashMultiplier">
  | undefined => {
  if (pointsBalance == null || rewardsAccountId == null) {
    return undefined;
  }

  // In the fixed-price (grid) flow the base fare is automatically paid in points
  // and deducted from the balance available for flex redemption.
  const pointsBalanceWithFixedFareDeducted = {
    ...pointsBalance.totalPoints,
    value:
      pointsBalance.totalPoints.value -
      (isFixedPrice ? baseFareFixedRewardsPrice?.value || 0 : 0),
  };

  if (pointsBalanceWithFixedFareDeducted.value < 0) {
    return undefined;
  }

  // Flex-redeemable ceiling in points: the backend `maximum` from
  // POST /api/v0/pointsToCash/quote, which prices every product (incl.
  // fintech add-ons like CFAR) at its correct per-SKU rate and already nets
  // wallet offers and credits (RBC-2289).
  if (pointsRedemptionMaximum == null) {
    return undefined;
  }

  if (pointsRedemptionMaximum <= 0) {
    return undefined;
  }

  return {
    totalPoints: {
      ...pointsBalanceWithFixedFareDeducted,
      value: Math.min(
        pointsRedemptionMaximum,
        pointsBalanceWithFixedFareDeducted.value
      ),
    },
    rewardsAccountId,
    isFixedPointsPricingEligible: pointsBalance.isFixedPointsPricingEligible,
  };
};
